// H-015: «Agregar adenda / rectificar». Un documento firmado no se edita: se corrige con una adenda firmada. Este diálogo pide al servidor el PDF de la adenda
// firmado con PAdES (`firmarDocumentoEnServidor`: nombre, registro y fecha del firmante los pone el servidor), lo guarda como Binary + DocumentReference
// (que apunta al original con `relatesTo: appends`) y crea ambos, DocumentReference y Provenance (que lo referencia en `entity`), en UNA transacción FHIR: o quedan los dos o ninguno; nunca actualiza ni borra el original. El permiso real lo impone el servidor (AccessPolicy + proxy): si
// rechaza, el error se muestra aquí y no se oculta.
import { formatHumanName, getStatus, OperationOutcomeError } from '@medplum/core';
import type { DocumentReference, Provenance } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { tituloDocumentoFirmado } from '@/lib/documentosFirmados';
import { EXT_FIRMA_FIRMADO_EN, EXT_FIRMA_SIGNATURE_REF } from '@/lib/documentosFirmados';
import { construirProvenanceAdenda, type CrearAdendaInput, MOTIVO_ADENDA_MIN } from '../lib/audit/bloqueo';
import { idTemporal, LoteTransaccional, recursoRespuesta } from '../formulario/discretos/transaccionFhir';
import { firmarDocumentoEnServidor } from './vaultClient';

export interface AdendaDialogProps {
  /** Documento original al que se vincula la adenda; `null` = diálogo cerrado. */
  readonly documento: DocumentReference | null;
  readonly onCerrar: () => void;
  readonly onCreada: (adenda: Provenance) => void;
}

/** El servidor (AccessPolicy / proxy) respondió 403: el rol no puede escribir Provenance. */
export function esErrorDePermiso(err: unknown): boolean {
  if (err instanceof OperationOutcomeError && getStatus(err.outcome) === 403) return true;
  const e = err as { status?: number; message?: string } | null;
  return e?.status === 403 || /\b403\b|forbidden/i.test(e?.message ?? '');
}

function nombreDelAutor(perfil: { name?: Parameters<typeof formatHumanName>[0][] } | undefined): string {
  const n = perfil?.name?.[0];
  return n ? formatHumanName(n) : '';
}

export function AdendaDialog({ documento, onCerrar, onCreada }: AdendaDialogProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const perfil = useMedplumProfile() as ({ resourceType?: string; id?: string; name?: Parameters<typeof formatHumanName>[0][] } & Record<string, unknown>) | undefined;
  const [motivo, setMotivo] = useState('');
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ahora] = useState(() => new Date());

  useEffect(() => {
    if (documento) {
      setMotivo('');
      setTexto('');
      setError(null);
    }
  }, [documento]);

  const motivoValido = motivo.trim().length >= MOTIVO_ADENDA_MIN;
  const textoValido = texto.trim().length > 0;
  const autorRef = perfil?.resourceType && perfil.id ? ({ reference: `${perfil.resourceType}/${perfil.id}`, ...(nombreDelAutor(perfil) ? { display: nombreDelAutor(perfil) } : {}) } as unknown as CrearAdendaInput['autor']) : undefined;
  const puedeGuardar = motivoValido && textoValido && !enviando && !!documento?.id && !!autorRef;

  async function guardar(): Promise<void> {
    if (!puedeGuardar || !documento?.id || !autorRef) return;
    setEnviando(true);
    setError(null);
    try {
      const refOriginal = `DocumentReference/${documento.id}`;
      const tituloOriginal = tituloDocumentoFirmado(documento);
      const refPaciente = documento.subject?.reference;
      // Sin paciente no hay `securityContext` para el Binary (regla de vínculo asistencial): se corta ANTES de firmar nada.
      if (!refPaciente?.startsWith('Patient/')) {
        setError(t('fichaPaciente.adenda.sinPaciente'));
        return;
      }
      const titulo = `${t('fichaPaciente.adenda.tituloDocumento')} — ${tituloOriginal}`;
      // El servidor arma el PDF y le pone el bloque del firmante (nombre, registro, fecha); además relee el original (y su paciente) con la sesión y pone él el paciente, el título y el renglón del original: el `paciente` de abajo es solo un placeholder que exige el esquema.
      const firmado = await firmarDocumentoEnServidor({
        documentoId: documento.id,
        tipoDocumento: 'adenda-clinica',
        especificacion: {
          modo: 'pares',
          titulo,
          paciente: { nombre: '—' },
          pares: [
            { pregunta: t('fichaPaciente.adenda.motivo'), respuesta: motivo.trim() },
            { pregunta: t('fichaPaciente.adenda.texto'), respuesta: texto.trim() },
          ],
        },
      });
      // Desde aquí el PDF ya está firmado: cualquier fallo deja un firmado sin vincular, y el mensaje lo dice (sin datos clínicos).
      let adenda: Provenance;
      try {
        const binario = await medplum.createBinary({
          data: firmado.signedPdf,
          contentType: 'application/pdf',
          filename: 'adenda-clinica-firmada.pdf',
          securityContext: { reference: refPaciente }, // MT-042: el paciente, para la regla de vínculo asistencial
        });
        const lote = new LoteTransaccional();
        const urnDocumento = idTemporal();
        lote.crear<DocumentReference>(
          {
            resourceType: 'DocumentReference',
            status: 'current',
            subject: { reference: refPaciente },
            type: { text: titulo },
            author: [{ reference: autorRef.reference as string }],
            relatesTo: [{ code: 'appends', target: { reference: refOriginal } }],
            content: [
              {
                attachment: {
                  contentType: 'application/pdf',
                  url: `Binary/${binario.id}`,
                  title: firmado.simulada ? `${titulo} — FIRMA SIMULADA (Security Data no configurado)` : `${titulo} firmado (Security Data)`,
                },
              },
            ],
            extension: [
              { url: EXT_FIRMA_SIGNATURE_REF, valueString: firmado.signatureRef },
              { url: EXT_FIRMA_FIRMADO_EN, valueDateTime: firmado.firmadoEn },
              { url: 'http://logiacare.ec/fhir/extension/firma-simulada', valueBoolean: firmado.simulada },
            ],
          },
          urnDocumento,
        );
        // Solo `entity` (no `target`): así la búsqueda de adendas por el original nunca cuenta la propia adenda firmada como adenda de sí misma.
        const provenance = construirProvenanceAdenda({
          target: { reference: refOriginal, display: tituloOriginal },
          motivo: motivo.trim(),
          texto: texto.trim(),
          autor: autorRef,
          documentoFirmado: { reference: urnDocumento },
        });
        lote.crear<Provenance>(provenance);
        const respuesta = await lote.enviar(medplum);
        adenda = recursoRespuesta<Provenance>(respuesta, 1, provenance);
      } catch {
        setError(t('fichaPaciente.adenda.sinVincular'));
        return;
      }
      onCreada(adenda);
    } catch (err) {
      setError(esErrorDePermiso(err) ? t('fichaPaciente.adenda.sinPermiso') : err instanceof Error ? err.message : String(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open={documento !== null} onOpenChange={(abierto) => !abierto && !enviando && onCerrar()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('fichaPaciente.adenda.titulo')}</DialogTitle>
          <DialogDescription>{t('fichaPaciente.adenda.descripcion')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void guardar();
          }}
        >
          <p className="text-sm">
            <span className="font-medium">{t('fichaPaciente.adenda.original')}: </span>
            {documento ? tituloDocumentoFirmado(documento) : ''}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('fichaPaciente.adenda.autor')}: {nombreDelAutor(perfil) || '—'} · {ahora.toLocaleString('es-EC')}
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="adenda-motivo">{t('fichaPaciente.adenda.motivo')}</Label>
            <Textarea id="adenda-motivo" required aria-required="true" aria-describedby="adenda-motivo-ayuda" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            <p id="adenda-motivo-ayuda" className="text-xs text-muted-foreground">
              {t('fichaPaciente.adenda.motivoAyuda', { min: MOTIVO_ADENDA_MIN })}
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="adenda-texto">{t('fichaPaciente.adenda.texto')}</Label>
            <Textarea id="adenda-texto" required aria-required="true" rows={5} value={texto} onChange={(e) => setTexto(e.target.value)} />
          </div>
          {!autorRef && <p className="text-sm text-amber-800">{t('fichaPaciente.adenda.sinAutor')}</p>}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {t('fichaPaciente.adenda.error')}: {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCerrar} disabled={enviando}>
              {t('fichaPaciente.adenda.cancelar')}
            </Button>
            <Button type="submit" disabled={!puedeGuardar}>
              {enviando ? t('fichaPaciente.adenda.guardando') : t('fichaPaciente.adenda.guardar')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
